import type { Grid, Sizes, NumColumns, ColumnMath } from './grid.types';

export const grid: Grid = {
  gridColumns: {
    xl: 12,
    lg: 12,
    md: 9,
    sm: 4,
  },
  col: {
    gutterSize: {
      xl: 48,
      lg: 32,
      md: 24,
      sm: 16,
    },
  },
  row: {
    gutterSize: {
      xl: 42,
      lg: 32,
      md: 24,
      sm: 24,
    },
  },
  container: {
    margin: {
      xl: 48,
      lg: 32,
      md: 24,
      sm: 16,
    },
  },
};

const columnMath: ColumnMath = <B extends Sizes>(
  breakpoint: B,
  {
    multiplier = 1,
    gridConfig = { gridColumns: grid.gridColumns },
    // eslint-disable-next-line no-console
    logErrorFn = console.error,
  } = {},
) => {
  const { gridColumns } = gridConfig;
  const result = gridColumns[breakpoint] * multiplier;

  // If attempting to divide by a number that doesn't evenly divide the column count,
  // return the full column count for that breakpoint instead
  if (result > gridColumns[breakpoint] || result !== Math.floor(result)) {
    const error = new Error(
      `Grid: Unsupported multiplier: ${multiplier} for the specified
         breakpoint: ${breakpoint}. Falling back to full column span.`,
    );
    logErrorFn(error);
    return gridColumns[breakpoint];
  }

  return result as NumColumns;
};

const fullColumns: ColumnMath = (breakpoint, options) => columnMath(breakpoint, options);

const threeQuartersColumns: ColumnMath<'3/4'> = (breakpoint, options) =>
  columnMath(breakpoint, { ...options, multiplier: 3 / 4 });

const twoThirdsColumns: ColumnMath<'2/3'> = (breakpoint, options) =>
  columnMath(breakpoint, { ...options, multiplier: 2 / 3 });

const halfColumns: ColumnMath<'1/2'> = (breakpoint, options) =>
  columnMath(breakpoint, { ...options, multiplier: 1 / 2 });

const thirdColumns: ColumnMath<'1/3'> = (breakpoint, options) =>
  columnMath(breakpoint, { ...options, multiplier: 1 / 3 });

const quarterColumns: ColumnMath<'1/4'> = (breakpoint, options) =>
  columnMath(breakpoint, { ...options, multiplier: 1 / 4 });

const sixthColumns: ColumnMath<'1/6'> = (breakpoint, options) =>
  columnMath(breakpoint, { ...options, multiplier: 1 / 6 });

export {
  columnMath,
  fullColumns,
  threeQuartersColumns,
  twoThirdsColumns,
  halfColumns,
  thirdColumns,
  quarterColumns,
  sixthColumns,
};
